import Card from '@mui/material/Card';
import React from 'react';
import { Styles } from '@t2c/shared-fe';
import Avatar from '@mui/material/Avatar';
import {
  AI_MEDIA_GENERATE_ERR_MSGS,
  BrollRenderStatus,
  CardTypeEnum,
  extractDate,
  extractTime,
} from '../../utils';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { ConfirmDialog } from '../../Common/dialog';
import { DropDownOptions, ProgressStuckNote, StatusWithProgressBar } from '../../Common';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { Iconify } from '@sdf/base';
import { useBrollClipCard } from '../../../t2c-fe/hooks/broll/useBrollClipCard';
import { AIMedia, AIMediaStatus, Status } from '../../../t2c-fe/@types';
import { RenameDialog } from '../text-to-speech/RenameDialog';
import { AvatarBox, FooterBox, StyledCard } from '../avatar-library/AvatarSection';
import { DeleteConfirmModal } from '../delete-confirm/DeleteConfirmModal';

interface Props {
  data: AIMedia;
  refreshBrollData: () => void;
  isImage?: boolean;
}

export const BrollClipCard: React.FC<Props> = ({ data, refreshBrollData, isImage = false }) => {
  const {
    menuAnchorEl,
    setMenuAnchorEl,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDeleteVideo,
    isDeletingVideo,
    menuActions,
    handleOnClick,
    status,
    overallPercentage,
    hideDropdownButton,
    closeRenameDialog,
    isEditNameLoading,
    handleOnRename,
    isRenameDialogOpen,
    brollClipStatus,
    brollClipUrl,
    cardProgress,
  } = useBrollClipCard({ videoData: data, refreshBrollData, isImage });

  const { id, name, thumbnailUrl, updatedAt, url, createdAt, prompt } = data;

  const isError = status === AIMediaStatus.Error;

  return (
    <>
      <StyledCard
        variant="outlined"
        status={status as AIMediaStatus}
        onClick={hideDropdownButton || isError ? () => null : handleOnClick}
      >
        <AvatarBox height="85%" status={status as AIMediaStatus} className="avatar-box">
          <Tooltip
            title={prompt?.length > 75 ? `${prompt.slice(0, 75)}...` : prompt}
            placement="bottom-start"
          >
            <Avatar alt={name} src={thumbnailUrl} sx={{ width: '152px', height: '152px' }}>
              <Box
                sx={{
                  width: '152px',
                  height: '152px',
                  background: (theme) => theme.palette.background.primary,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Iconify
                  icon={'ooui:user-avatar'}
                  width={'24px'}
                  height={'24px'}
                  mr={0.5}
                  color={(theme) => theme.palette.text.primary}
                />
              </Box>
            </Avatar>
          </Tooltip>
          <Stack
            direction="row"
            gap={0.5}
            sx={{
              position: 'absolute',
              top: '10px',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {status === AIMediaStatus.Error ? (
              <Tooltip
                title={
                  AI_MEDIA_GENERATE_ERR_MSGS.includes(data?.errorMessage)
                    ? data?.errorMessage
                    : 'We hit a glitch in the matrix! Your video didn’t make it. No credit deducted. Give it another shot!'
                }
              >
                <Box
                  sx={{
                    background: (theme) => theme.palette.gradients.gradient8,
                    padding: '0 12px',
                    borderTopRightRadius: '20px',
                    borderBottomRightRadius: '20px',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Iconify
                    icon={'heroicons-outline:exclamation'}
                    width={'20px'}
                    height={'20px'}
                    color={(theme) => theme.palette.text.primary}
                  />
                  <span>Failed </span>
                </Box>
              </Tooltip>
            ) : (
              status !== AIMediaStatus.Success && (
                <Box
                  sx={{
                    padding: '0 12px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ProgressStuckNote showToolTip={true} />
                </Box>
              )
            )}
            <div></div>
            {!hideDropdownButton && (
              <DropDownOptions
                menuOptions={menuActions}
                anchorEl={menuAnchorEl}
                setAnchorEl={setMenuAnchorEl}
              />
            )}
          </Stack>
          <Stack
            sx={{
              minWidth: 150,
              width: '40%',
              position: 'absolute',
              top: '65%',
            }}
          >
            <StatusWithProgressBar
              progressPercentage={cardProgress}
              status={status}
              isBrollCaption
              showStatusOnSuccess={false}
            />
          </Stack>
        </AvatarBox>
        <FooterBox status={status} className="avatar-footer">
          <Tooltip title={name}>
            <Typography
              sx={{
                display: 'inline-block',
                maxWidth: '50%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              className="avatar-name"
              fontWeight={400}
              fontSize={14}
            >
              {name ?? '--'}
            </Typography>
          </Tooltip>
          <Typography
            className="avatar-time"
            variant="caption"
            sx={{
              fontSize: 12,
            }}
          >
            {extractDate(updatedAt ?? createdAt)}
            &nbsp;
            {extractTime(updatedAt ?? createdAt)}
          </Typography>
        </FooterBox>
      </StyledCard>
      <DeleteConfirmModal
        handleCancel={closeDeleteDialog}
        open={isDeleteDialogOpen}
        id={id}
        name={name}
        refetchData={refreshBrollData}
        type={CardTypeEnum.AIMedia}
      />
      <RenameDialog
        shouldOpenDialog={isRenameDialogOpen}
        handleCloseDialog={closeRenameDialog}
        initialName={name || ''}
        handleOnRename={handleOnRename}
        isLoading={isEditNameLoading}
        dialogTitle="Rename"
      />
    </>
  );
};

const styles: Styles = {
  card: {
    width: 350,
  },
};
